import type { BillingAddress } from "../../../src/shared/billing-location";
import apiClient from "@/api/client";

// --- Types ---

export type CancelReason =
  | "too_expensive"
  | "not_using"
  | "missing_feature"
  | "switching_tools"
  | "other";

export interface UsageSummary {
  plan: string;
  billingCycle: string;
  rendersUsed: number;
  rendersLimit: number;
  currentPeriodStart: string;
  currentPeriodEnd: string;
}

export interface RecentRender {
  id: string;
  status: string;
  inputType: string;
  pageCount: number | null;
  fileSize: number | null;
  renderTimeMs: number | null;
  totalTimeMs: number | null;
  isTest: boolean;
  createdAt: string;
  completedAt: string | null;
  downloadToken: string | null;
}

export interface OverviewData {
  usage: UsageSummary;
  recentRenders: RecentRender[];
  dailyUsage: Array<{ date: string; count: number }>;
}

export interface ApiKeyData {
  id: string;
  name: string;
  keyPrefix: string;
  type: "live" | "test";
  lastUsedAt: string | null;
  createdAt: string;
  revokedAt: string | null;
}

export interface ApiKeyItem {
  id: string;
  name: string;
  keyPrefix: string;
  type: "live" | "test";
  lastUsedAt: string | null;
  createdAt: string;
  revokedAt: string | null;
}

export interface ApiKeyCreateResponse {
  id: string;
  key: string;
  keyPrefix: string;
  type: "live" | "test";
  name: string;
}

export interface RenderLogEntry {
  id: string;
  status: string;
  inputType: string;
  pageCount: number | null;
  fileSize: number | null;
  renderTimeMs: number | null;
  totalTimeMs: number | null;
  downloadToken: string | null;
  isTest: boolean;
  createdAt: string;
  completedAt: string | null;
}

export interface UsageHistoryResponse {
  renders: RenderLogEntry[];
  total: number;
  page: number;
  pageSize: number;
}

export interface BillingInfo {
  plan: string;
  billingCycle: string;
  subscriptionStatus: string | null;
  currentPeriodEnd: string | null;
  trialEndDate: string | null;
  hasPaymentMethod: boolean;
  cardLast4: string | null;
  cardBrand: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  effectiveCancelAtPeriodEnd: boolean;
  rendersUsed: number;
  rendersLimit: number;
  // PDF-13: dunning state for the BillingPage past_due alert.
  // pastDueSince is when the current dunning episode began;
  // gracePeriodEndDate is the cancellation deadline (server pre-computes
  // pastDueSince + DUNNING_GRACE_DAYS so the dashboard renders one
  // canonical date without knowing the grace-day constant).
  pastDueSince: string | null;
  gracePeriodEndDate: string | null;
}

// CHK-39: `features` intentionally absent — the checkout plans API no longer
// returns it; the UI renders feature bullets from the local catalog.
export interface CheckoutPlan {
  name: string;
  slug: string;
  monthlyPriceCents: number;
  annualPriceCents: number | null;
  active: boolean;
  isEnterprise?: boolean;
}

export interface InvoiceLineItem {
  description: string;
  amountCents: number;
  quantity: number;
}

export interface InvoiceItem {
  id: number;
  createdAt: string;
  amountCents: number;
  type: "subscription" | "overage" | "one_time";
  status: "paid" | "failed" | "pending" | "refunded";
  periodStart: string;
  periodEnd: string;
  lineItems: InvoiceLineItem[];
}

export interface BillingHistoryResponse {
  invoices: InvoiceItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ResendReceiptResponse {
  sent: true;
  sentAt: string;
}

// --- API calls ---

export async function getOverview(): Promise<OverviewData> {
  const { data } = await apiClient.get<OverviewData>("/dashboard/overview");
  return data;
}

export async function getApiKeys(): Promise<ApiKeyItem[]> {
  const { data } = await apiClient.get<ApiKeyItem[]>("/dashboard/api-keys");
  return data;
}

export async function renameApiKey(keyId: string, name: string): Promise<void> {
  await apiClient.patch(`/dashboard/api-keys/${keyId}`, { name });
}

export async function createApiKey(
  name: string,
  type: "live" | "test",
): Promise<ApiKeyCreateResponse> {
  const { data } = await apiClient.post<ApiKeyCreateResponse>(
    "/dashboard/api-keys",
    { name, type },
  );
  return data;
}

export async function revokeApiKey(keyId: string): Promise<void> {
  await apiClient.delete(`/dashboard/api-keys/${keyId}`);
}

export async function getUsageHistory(params: {
  page?: number;
  pageSize?: number;
  startDate?: string;
  endDate?: string;
  status?: string;
  apiKeyId?: string;
  jobId?: string;
}): Promise<UsageHistoryResponse> {
  const { data } = await apiClient.get<UsageHistoryResponse>(
    "/dashboard/usage",
    { params },
  );
  return data;
}

export async function getBilling(): Promise<BillingInfo> {
  const { data } = await apiClient.get<BillingInfo>("/dashboard/billing");
  return data;
}

export async function getBillingHistory(params: {
  page?: number;
  pageSize?: number;
}): Promise<BillingHistoryResponse> {
  const { data } = await apiClient.get<BillingHistoryResponse>(
    "/dashboard/billing/history",
    {
      params,
    },
  );
  return data;
}

export async function resendReceipt(
  invoiceId: number,
): Promise<ResendReceiptResponse> {
  const { data } = await apiClient.post<ResendReceiptResponse>(
    `/dashboard/billing/history/${invoiceId}/resend-receipt`,
  );
  return data;
}

export async function getPlans(): Promise<CheckoutPlan[]> {
  const { data } = await apiClient.get<CheckoutPlan[]>("/dashboard/plans");
  return data;
}

// --- Checkout / Billing API ---

export interface SubscribeRequest {
  planSlug: string;
  billingCycle: "monthly" | "annual";
  cardToken?: string;
  withTrial?: boolean;
  cardLast4?: string;
  cardBrand?: string;
  activateOn?: string;
  /**
   * Required whenever the subscription bills immediately — which is everything
   * except a cardless trial, so plan changes and resubscribe need it too even
   * though no card is attached on those paths. The server rejects the request
   * without it (see the willBillNow gate in routes/dashboard.ts).
   */
  billingAddress?: BillingAddress;
}

export interface SubscribeResult {
  subscription: {
    id: string;
    status: string;
    plan: string;
    billingCycle: string;
  };
  customer: { id: string; hasCard: boolean };
}

export interface UpdateCardResult {
  customer: { id: string; hasCard: boolean };
  subscription: { id: string; status: string; plan: string } | null;
}

export async function getCheckoutConfig(): Promise<{
  checkoutToken: string;
  secretToken: string;
}> {
  const { data } = await apiClient.get<{
    checkoutToken: string;
    secretToken: string;
  }>("/dashboard/checkout-config");
  return data;
}

export async function prepareCheckout(billingAddress?: BillingAddress): Promise<{
  checkoutToken: string;
  secretToken: string;
}> {
  // Sent at prepare time so the location lands on the Helcim customer record —
  // where recurring tax is computed — before a card is attached to it.
  const { data } = await apiClient.post<{
    checkoutToken: string;
    secretToken: string;
  }>("/dashboard/prepare-checkout", billingAddress ? { billingAddress } : {});
  return data;
}

/** The address checkout already holds, or null. Prefill only — never throws. */
export async function getBillingAddress(): Promise<BillingAddress | null> {
  try {
    const { data } = await apiClient.get<{ billingAddress: BillingAddress | null }>(
      "/dashboard/billing-address",
    );
    return data.billingAddress ?? null;
  } catch {
    return null;
  }
}

/** Persist the billing address; `deferred` means no Helcim customer exists yet. */
export async function saveBillingAddress(
  address: BillingAddress,
): Promise<{ success: boolean; deferred?: boolean }> {
  const { data } = await apiClient.put<{ success: boolean; deferred?: boolean }>(
    "/dashboard/billing-address",
    address,
  );
  return data;
}

export async function subscribe(
  req: SubscribeRequest,
): Promise<SubscribeResult> {
  const { data } = await apiClient.post<SubscribeResult>(
    "/dashboard/subscribe",
    req,
  );
  return data;
}

export async function cancelSubscription(opts: {
  reason?: CancelReason;
  reasonDetail?: string;
}): Promise<{ success: true; accessUntil: string }> {
  const { data } = await apiClient.post<{ success: true; accessUntil: string }>(
    "/dashboard/subscription/cancel",
    opts,
  );
  return data;
}

export async function updateCard(
  cardToken: string,
  cardLast4?: string | null,
  cardBrand?: string | null,
): Promise<UpdateCardResult> {
  const { data } = await apiClient.post<UpdateCardResult>(
    "/dashboard/update-card",
    {
      cardToken,
      ...(cardLast4 && { cardLast4 }),
      ...(cardBrand && { cardBrand }),
    },
  );
  return data;
}

export interface UserSettings {
  email: string;
  displayName: string | null;
  notifyQuota80: boolean;
  notifyQuota90: boolean;
  notifyQuota100: boolean;
  notifyFailedRenders: boolean;
  notifyBillingEvents: boolean;
}

export async function getSettings(): Promise<UserSettings> {
  const { data } = await apiClient.get<UserSettings>("/dashboard/settings");
  return data;
}

export async function updateSettings(
  settings: Partial<Omit<UserSettings, "email">>,
): Promise<void> {
  await apiClient.put("/dashboard/settings", settings);
}

export interface WebhookEndpoint {
  id: string;
  url: string;
  secret: string;
  events: string;
  enabled: boolean;
  createdAt: string;
}

export async function getWebhooks(): Promise<WebhookEndpoint[]> {
  const { data } = await apiClient.get<WebhookEndpoint[]>(
    "/dashboard/webhooks",
  );
  return data;
}

export async function createWebhook(
  url: string,
  events?: string,
): Promise<WebhookEndpoint> {
  const { data } = await apiClient.post<WebhookEndpoint>(
    "/dashboard/webhooks",
    { url, events },
  );
  return data;
}

export async function updateWebhook(
  id: string,
  data: { url?: string; events?: string; enabled?: boolean },
): Promise<WebhookEndpoint> {
  const { data: result } = await apiClient.put<WebhookEndpoint>(
    `/dashboard/webhooks/${id}`,
    data,
  );
  return result;
}

export async function deleteWebhook(id: string): Promise<void> {
  await apiClient.delete(`/dashboard/webhooks/${id}`);
}
